import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import {
  PhoneCall, PhoneOff, Upload, Loader2, Radio, Square, Pause, Play,
  Voicemail, FileAudio, ListChecks, ChevronRight, Minus, Plus as PlusIcon,
  Mic, StopCircle, Hash, MessageSquare, User, ExternalLink, Trash2, Pencil, AlertTriangle,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import useDialerSocket from '../../hooks/useDialerSocket';
import { LogCallDialog } from '../../components/LogCallDialog';
import { useAppMessage } from '../../components/AppMessage';
import AudioDeviceBar from '../../components/AudioDeviceBar';

const API = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const STATUS_META = {
  queued:            { badge: 'bg-slate-100 text-slate-600',  label: 'Queued',    pulse: false },
  dialing:           { badge: 'bg-yellow-100 text-yellow-800', label: 'Dialing',   pulse: true  },
  ringing:           { badge: 'bg-blue-100 text-blue-800',     label: 'Ringing',   pulse: true  },
  answered:          { badge: 'bg-green-100 text-green-800',   label: 'Answered',  pulse: false },
  parked:            { badge: 'bg-green-100 text-green-900',   label: 'HUMAN',     pulse: true  },
  barge:             { badge: 'bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer', label: 'BARGE', pulse: true },
  handed_off:        { badge: 'bg-emerald-100 text-emerald-800', label: 'On Call', pulse: false },
  voicemail_dropped: { badge: 'bg-orange-100 text-orange-800', label: 'VM Drop',   pulse: false },
  bad_number:        { badge: 'bg-red-100 text-red-800',       label: 'Bad #',     pulse: false },
  no_answer:         { badge: 'bg-slate-100 text-slate-600',   label: 'No Ans',    pulse: false },
  busy:              { badge: 'bg-slate-100 text-slate-600',   label: 'Busy',      pulse: false },
  cancelled:         { badge: 'bg-slate-100 text-slate-500',   label: 'Cancelled', pulse: false },
  error:             { badge: 'bg-red-100 text-red-800',       label: 'Error',     pulse: false },
};

function secondsSince(iso) {
  if (!iso) return 0;
  try { return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000)); } catch { return 0; }
}
function fmtTime(s) {
  const m = Math.floor(s / 60), ss = s % 60;
  return `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
}
function timeOf(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString([], { hour12: false }); } catch { return '—'; }
}

const EMPTY_CALL_FORM = {
  phone_number: '', notes: '', call_outcome: 'connected', duration_seconds: 0,
  schedule_followup: false, followup_date: '', followup_time: '09:00',
  mark_dnc: false, send_email_after: false, email_template_id: '', recording_id: '',
};

const CLIP_STORAGE_KEYS = {
  voicemail: 'jtcrm:dialer:voicemailId',
  greeting:  'jtcrm:dialer:greetingId',
  greeting2: 'jtcrm:dialer:greeting2Id',
};
const AI_AGENT_STORAGE_KEY = 'jtcrm:dialer:aiAgentEnabled';

export default function DialerPage() {
  const [library, setLibrary] = useState({ voicemail: [], greeting: [], greeting2: [] });
  const [lists, setLists] = useState([]);
  const [vmId, setVmId] = useState('');
  const [greetingId, setGreetingId] = useState('');
  const [greeting2Id, setGreeting2Id] = useState('');
  const [listId, setListId] = useState('');
  const [concurrent, setConcurrent] = useState(4);
  const [aiAgentEnabled, setAiAgentEnabled] = useState(() => {
    try { return localStorage.getItem(AI_AGENT_STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [uploading, setUploading] = useState({ voicemail: false, greeting: false, greeting2: false });
  // attempt_id → unix-ms timestamp when greeting1 finished playing
  const [greetingEndedAt, setGreetingEndedAt] = useState({});
  const [source, setSource] = useState('list'); // 'list' | 'numbers'
  const [numbersText, setNumbersText] = useState('');

  const [campaignId, setCampaignId] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const activeCallRef = useRef(null);
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  const [callMeta, setCallMeta] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [callForm, setCallForm] = useState(null);
  const [savingLog, setSavingLog] = useState(false);
  const [, forceTick] = useState(0);

  const { connected, liveEvents } = useDialerSocket(campaignId);

  const fetchAll = useCallback(async () => {
    try {
      const [vm, gr, g2, ls, tn] = await Promise.all([
        axios.get(`${API}/api/ai-calls/audio-media?kind=voicemail`),
        axios.get(`${API}/api/ai-calls/audio-media?kind=greeting`),
        axios.get(`${API}/api/ai-calls/audio-media?kind=greeting2`),
        axios.get(`${API}/api/ai-calls/dialer/lists`),
        axios.get(`${API}/api/ai-calls/dialer/test-numbers`),
      ]);
      const vmList = vm.data || [];
      const grList = gr.data || [];
      const g2List = g2.data || [];
      setLibrary({ voicemail: vmList, greeting: grList, greeting2: g2List });
      setLists(ls.data || []);
      const saved = (tn.data?.numbers || []);
      if (saved.length && !numbersText) setNumbersText(saved.join('\n'));

      const savedVm = localStorage.getItem(CLIP_STORAGE_KEYS.voicemail);
      const savedGr = localStorage.getItem(CLIP_STORAGE_KEYS.greeting);
      // Migrate the old key (pitchId) to the new one (greeting2Id) once
      const legacyPitchId = localStorage.getItem('jtcrm:dialer:pitchId');
      const savedG2 = localStorage.getItem(CLIP_STORAGE_KEYS.greeting2) || legacyPitchId;
      if (savedVm && vmList.some(v => v.id === savedVm)) setVmId(prev => prev || savedVm);
      if (savedGr && grList.some(g => g.id === savedGr)) setGreetingId(prev => prev || savedGr);
      if (savedG2 && g2List.some(g => g.id === savedG2)) setGreeting2Id(prev => prev || savedG2);
    } catch (e) {
      toast.error('Failed to load dialer data');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (!campaignId) return;
    const id = setInterval(() => forceTick(x => x + 1), 1000);
    return () => clearInterval(id);
  }, [campaignId]);

  useEffect(() => { if (vmId)        localStorage.setItem(CLIP_STORAGE_KEYS.voicemail, vmId); },        [vmId]);
  useEffect(() => { if (greetingId)  localStorage.setItem(CLIP_STORAGE_KEYS.greeting,  greetingId); },  [greetingId]);
  useEffect(() => { if (greeting2Id) localStorage.setItem(CLIP_STORAGE_KEYS.greeting2, greeting2Id); }, [greeting2Id]);
  useEffect(() => { try { localStorage.setItem(AI_AGENT_STORAGE_KEY, aiAgentEnabled ? '1' : '0'); } catch {} }, [aiAgentEnabled]);

  // Auto-resume: if this user already has a live session, jump straight to
  // the live view instead of forcing them to re-pick clips on every refresh.
  useEffect(() => {
    if (campaignId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/ai-calls/dialer/active-session`);
        if (!cancelled && r.data?.campaign_id) setCampaignId(r.data.campaign_id);
      } catch { /* fine — no active session */ }
    })();
    return () => { cancelled = true; };
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId) return;
    let cancel = false;
    let lastJSON = '';
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/ai-calls/campaigns/${campaignId}/live-session/metrics`);
        if (cancel) return;
        const next = JSON.stringify(r.data);
        if (next !== lastJSON) {
          lastJSON = next;
          setMetrics(r.data);
        }
      } catch {}
    };
    load();
    const id = setInterval(load, 2000);
    return () => { cancel = true; clearInterval(id); };
  }, [campaignId]);

  // Chime when a human is detected
  const lastAlerted = useRef(null);
  useEffect(() => {
    const last = liveEvents[liveEvents.length - 1];
    if (!last || last.type !== 'dialer.human_answered') return;
    if (last.campaign_id !== campaignId) return;
    if (lastAlerted.current === last.attempt_id) return;
    lastAlerted.current = last.attempt_id;
    let ctx;
    let closeTimer;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = 880; g.gain.value = 0.09;
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.22);
      closeTimer = setTimeout(() => ctx.close(), 400);
    } catch {}
    return () => {
      if (closeTimer) clearTimeout(closeTimer);
      if (ctx && ctx.state !== 'closed') { try { ctx.close(); } catch {} }
    };
  }, [liveEvents, campaignId]);

  const clearLocalSelection = (kind, clipId) => {
    if (kind === 'voicemail' && vmId === clipId) setVmId('');
    else if (kind === 'greeting' && greetingId === clipId) setGreetingId('');
    else if (kind === 'greeting2' && greeting2Id === clipId) setGreeting2Id('');
  };

  const deleteClip = async (kind, clip, { force = false } = {}) => {
    try {
      const url = `${API}/api/ai-calls/audio-media/${clip.id}${force ? '?force=true' : ''}`;
      const r = await axios.delete(url);
      const cleared = r.data?.cleared_campaign_refs || 0;
      toast.success(cleared > 0 ? `Deleted "${clip.name}" — removed from ${cleared} campaign ref(s)` : `Deleted "${clip.name}"`);
      clearLocalSelection(kind, clip.id);
      await fetchAll();
      return { ok: true };
    } catch (e) {
      const status = e.response?.status;
      if (status === 409) {
        return { ok: false, conflict: true, detail: e.response?.data?.detail };
      }
      const detail = e.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Delete failed');
      toast.error(message);
      return { ok: false };
    }
  };

  const renameClip = async (clip, nextName) => {
    try {
      await axios.patch(`${API}/api/ai-calls/audio-media/${clip.id}`, { name: nextName });
      toast.success(`Renamed to "${nextName}"`);
      await fetchAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
      return false;
    }
  };

  const fetchClipCampaigns = async (clip) => {
    try {
      const r = await axios.get(`${API}/api/ai-calls/audio-media/${clip.id}/campaigns`);
      return r.data?.campaigns || [];
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load campaigns');
      return [];
    }
  };

  const uploadClip = async (kind, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File must be ≤ 2 MB'); return; }
    setUploading(p => ({ ...p, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append('audio', file);
      fd.append('kind', kind);
      fd.append('name', file.name.replace(/\.[^.]+$/, ''));
      const r = await axios.post(`${API}/api/ai-calls/audio-media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!r.data.telnyx_upload_ok) {
        toast.error('Saved locally but Telnyx upload failed — check TELNYX_API_KEY');
        return;
      }
      const labelByKind = { voicemail: 'Voicemail', greeting: 'Greetings1', greeting2: 'Greetings2' };
      toast.success(`${labelByKind[kind] || kind} uploaded`);
      if (kind === 'voicemail') setVmId(r.data.id);
      else if (kind === 'greeting') setGreetingId(r.data.id);
      else setGreeting2Id(r.data.id);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(p => ({ ...p, [kind]: false }));
    }
  };

  const parsedNumbers = numbersText
    .split(/[\s,;]+/)
    .map(s => s.trim())
    .filter(Boolean);

  const canStart = !campaignId;

  const startCalls = async () => {
    try {
      const body = {
        voicemail_media_id: vmId,
        greeting_media_id: greetingId,
        max_concurrent_calls: concurrent,
        ai_agent_enabled: !!aiAgentEnabled,
      };
      if (greeting2Id) body.greeting2_media_id = greeting2Id;
      if (source === 'list') {
        body.list_id = listId;
      } else {
        body.phone_numbers = parsedNumbers;
        // persist so they come back next time
        axios.put(`${API}/api/ai-calls/dialer/test-numbers`, { numbers: parsedNumbers }).catch(() => {});
      }
      const r = await axios.post(`${API}/api/ai-calls/dialer/quick-launch`, body);
      setCampaignId(r.data.campaign_id);
      toast.success(`Dialing ${r.data.lead_count} numbers at ${concurrent} concurrent`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Launch failed');
    }
  };

  const SESSION_LABELS = { stop: 'stopped', pause: 'Paused', resume: 'Resumed' };
  const sessionAction = async (action) => {
    if (!campaignId) return;
    try {
      await axios.post(`${API}/api/ai-calls/campaigns/${campaignId}/live-session/${action}`);
      if (action === 'stop') {
        setCampaignId(null);
        setMetrics(null);
        setActiveCall(null);
        toast('Session stopped');
      } else {
        toast(SESSION_LABELS[action]);
      }
    } catch {
      toast.error(`${action[0].toUpperCase() + action.slice(1)} failed`);
    }
  };
  const stopSession   = () => sessionAction('stop');
  const pauseSession  = () => sessionAction('pause');
  const resumeSession = () => sessionAction('resume');

  const leadFetchAbortRef = useRef(null);
  const answerCall = async (attemptId) => {
    const att = (metrics?.in_flight || []).find(a => a.id === attemptId);
    const meta = {
      phone: att?.to_number || '',
      leadName: att?.lead_name || 'Lead',
      companyName: att?.lead_company || '',
      leadId: att?.lead_id || null,
    };

    // Tell InboundCallManager to silently auto-accept the bridged call that
    // Telnyx is about to deliver — no ringtone, no popup.
    window.dispatchEvent(new CustomEvent('jtcrm-dialer-expect-bridge', {
      detail: {
        attemptId,
        expiresAt: Date.now() + 30000,
        leadId: meta.leadId,
        phone: meta.phone,
        name: meta.leadName,
        company: meta.companyName,
      },
    }));

    try {
      await axios.post(`${API}/api/ai-calls/attempts/${attemptId}/answer`);
      setCallMeta(meta);
      setActiveLead(null);
      setActiveCall({
        attempt_id: attemptId,
        leadName: meta.leadName,
        phone: meta.phone,
        companyName: meta.companyName,
        startedAt: Date.now(),
      });
      if (meta.leadId) {
        leadFetchAbortRef.current?.abort();
        const ctrl = new AbortController();
        leadFetchAbortRef.current = ctrl;
        axios.get(`${API}/api/leads/${meta.leadId}`, { signal: ctrl.signal })
          .then(lr => setActiveLead(lr.data))
          .catch(() => {});
      }
    } catch (e) {
      // Cancel the expected-bridge signal — no call is coming
      window.dispatchEvent(new CustomEvent('jtcrm-dialer-expect-bridge', { detail: { expiresAt: 0 } }));
      toast.error(e.response?.data?.detail || 'Answer failed');
    }
  };

  const releaseCall = async (attemptId) => {
    try {
      await axios.post(`${API}/api/ai-calls/attempts/${attemptId}/release`);
      toast('Call ended');
    } catch {
      toast.error('Hangup failed');
    }
  };

  // Fired when the rep's WebRTC bridge fails (Telnyx couldn't reach the
  // registered client, etc.). Tear the called party's leg down immediately —
  // they already heard greeting1, no point dropping voicemail on top.
  const hangupOnWebRtcError = async (attemptId, errMsg) => {
    try {
      await axios.post(`${API}/api/ai-calls/attempts/${attemptId}/hangup`);
    } catch {/* best-effort */}
    toast.error(`Couldn't join the call — ${errMsg || 'WebRTC failed'}`);
  };

  const handleCallEnded = (duration, recordingId) => {
    setCallForm({
      ...EMPTY_CALL_FORM,
      phone_number: callMeta?.phone || '',
      duration_seconds: duration || 0,
      recording_id: recordingId || '',
    });
    setActiveCall(null);
    setLogCallOpen(true);
  };

  // Listen for bridge lifecycle events from InboundCallManager
  useEffect(() => {
    const onBridgeEnded = (e) => {
      const aid = e.detail?.attemptId;
      const startedAt = activeCallRef.current?.startedAt || Date.now();
      const duration = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      if (!aid || aid !== activeCallRef.current?.attempt_id) return;
      handleCallEnded(duration, null);
    };
    const onBridgeFailed = (e) => {
      const aid = e.detail?.attemptId;
      if (!aid) return;
      setActiveCall(null);
      hangupOnWebRtcError(aid, e.detail?.error);
    };
    window.addEventListener('jtcrm-dialer-bridge-ended', onBridgeEnded);
    window.addEventListener('jtcrm-dialer-bridge-failed', onBridgeFailed);
    return () => {
      window.removeEventListener('jtcrm-dialer-bridge-ended', onBridgeEnded);
      window.removeEventListener('jtcrm-dialer-bridge-failed', onBridgeFailed);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogCallClose = () => {
    setLogCallOpen(false);
    setCallForm(null);
    setActiveLead(null);
    setCallMeta(null);
  };

  const handleLogCallSubmit = async () => {
    if (!callForm?.phone_number) { toast.error('Phone number is required'); return; }
    setSavingLog(true);
    try {
      await axios.post(`${API}/api/communications/call`, {
        lead_id: callMeta?.leadId || null,
        phone_number: callForm.phone_number,
        notes: callForm.notes,
        call_outcome: callForm.call_outcome,
        duration_seconds: callForm.duration_seconds,
        recording_id: callForm.recording_id || undefined,
      });
      if (callForm.schedule_followup && callMeta?.leadId) {
        const dueDate = callForm.followup_date
          ? new Date(callForm.followup_date + 'T' + (callForm.followup_time || '09:00'))
          : (() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0); return d; })();
        await axios.post(`${API}/api/tasks`, {
          lead_id: callMeta.leadId,
          description: `Follow-up call with ${activeLead?.first_name || ''} ${activeLead?.last_name || ''}`.trim() || 'Follow-up call',
          due_date: dueDate.toISOString(),
        });
      }
      if (callForm.mark_dnc && callMeta?.leadId) {
        await axios.put(`${API}/api/leads/${callMeta.leadId}`, { dnc: true });
        toast.success('Call logged & lead marked DNC');
      } else {
        toast.success('Call logged');
      }
      handleLogCallClose();
    } catch {
      toast.error('Failed to log call');
    } finally {
      setSavingLog(false);
    }
  };

  const running = Boolean(campaignId && metrics?.campaign?.live_session_state !== 'idle');
  const sessionState = metrics?.campaign?.live_session_state || 'idle';
  const inFlight = metrics?.in_flight || [];
  const parked = inFlight.filter(a => a.status === 'parked' && !a.handed_off_at);
  const recent = metrics?.recent_dispositions || [];
  const pacing = metrics?.recent_pacing_log || [];
  const vmRate = pacing[0] ? Math.round(pacing[0].voicemail_rate * 100) : 0;
  const humansReached = recent.filter(r => ['answered','handed_off','parked','meeting_booked','demo_scheduled','callback_requested','not_interested','do_not_call','disconnected_early'].includes(r.status)).length
    + inFlight.filter(a => ['parked','handed_off','answered'].includes(a.status)).length;
  const vmDropped = recent.filter(r => r.status === 'voicemail_dropped').length
    + inFlight.filter(a => a.status === 'voicemail_dropped').length;
  const badCount = recent.filter(r => r.status === 'bad_number').length;

  const selectedList = lists.find(l => l.id === listId);
  const selectedVm = library.voicemail.find(v => v.id === vmId);
  const selectedGreeting = library.greeting.find(g => g.id === greetingId);
  const selectedGreeting2 = library.greeting2.find(g => g.id === greeting2Id);

  // Track dialer.greeting_ended events so the BARGE pill window can open
  useEffect(() => {
    const last = liveEvents[liveEvents.length - 1];
    if (!last || last.type !== 'dialer.greeting_ended') return;
    if (last.campaign_id !== campaignId) return;
    setGreetingEndedAt(prev => ({ ...prev, [last.attempt_id]: Date.now() }));
  }, [liveEvents, campaignId]);

  const operatorBusy = inFlight.some(a => a.status === 'handed_off' && !a.ended_at);
  const bargeWindowMs = 3000;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <style>{`
        @keyframes dialer-pulse-ring {
          0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.45); }
          50%     { box-shadow: 0 0 0 10px rgba(34,197,94,0); }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <PhoneCall className="h-6 w-6 text-primary" /> Dialer
          </h1>
          <p className="text-sm text-slate-500">
            Upload a voicemail + Greetings1 (and optional Greetings2 fallback), pick a list, then start concurrent calls.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={running ? 'border-green-300 text-green-700 bg-green-50' : 'border-slate-200 text-slate-500'}>
            <Radio className={`h-3 w-3 mr-1 ${connected ? 'text-green-500' : 'text-slate-300'}`} />
            {running ? sessionState.replace('_', ' ').toUpperCase() : 'IDLE'}
          </Badge>
        </div>
      </div>

      {/* Audio device selection — picks mic/speaker the WebRTC call will use */}
      <AudioDeviceBar />

      {/* HUMAN alert */}
      {parked.length > 0 && !activeCall && (
        <HumanAlert
          parked={parked[0]}
          bargeGrace={metrics?.campaign?.barge_grace_seconds ?? 3}
          greetingEndedAt={greetingEndedAt[parked[0].id]}
          onAnswer={answerCall}
          onRelease={releaseCall}
        />
      )}

      {/* Setup: expanded when idle, breadcrumb when running */}
      {!running ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <SetupCard
            n={1}
            title="Voicemail"
            subtitle="Plays when call hits an answering machine"
            icon={<Voicemail className="h-5 w-5 text-orange-500" />}
            clips={library.voicemail}
            currentId={vmId}
            onPick={setVmId}
            uploading={uploading.voicemail}
            onUpload={(f) => uploadClip('voicemail', f)}
            onRecord={(blob) => uploadClip('voicemail', new File([blob], `voicemail-${Date.now()}.webm`, { type: 'audio/webm' }))}
            onDelete={(clip, opts) => deleteClip('voicemail', clip, opts)}
            onRename={renameClip}
            onLoadCampaigns={fetchClipCampaigns}
          />
          <SetupCard
            n={2}
            title="Greeting"
            subtitle="First thing a live human hears"
            icon={<MessageSquare className="h-5 w-5 text-sky-500" />}
            clips={library.greeting}
            currentId={greetingId}
            onPick={setGreetingId}
            uploading={uploading.greeting}
            onUpload={(f) => uploadClip('greeting', f)}
            onRecord={(blob) => uploadClip('greeting', new File([blob], `greeting-${Date.now()}.webm`, { type: 'audio/webm' }))}
            onDelete={(clip, opts) => deleteClip('greeting', clip, opts)}
            onRename={renameClip}
            onLoadCampaigns={fetchClipCampaigns}
          />
          <SetupCard
            n={3}
            title="Greetings2"
            subtitle="Plays if you can't barge in (busy or 3s window expired)"
            icon={<FileAudio className="h-5 w-5 text-indigo-500" />}
            clips={library.greeting2}
            currentId={greeting2Id}
            onPick={setGreeting2Id}
            uploading={uploading.greeting2}
            onUpload={(f) => uploadClip('greeting2', f)}
            onRecord={(blob) => uploadClip('greeting2', new File([blob], `greeting2-${Date.now()}.webm`, { type: 'audio/webm' }))}
            onDelete={(clip, opts) => deleteClip('greeting2', clip, opts)}
            onRename={renameClip}
            onLoadCampaigns={fetchClipCampaigns}
          />
          <TargetCard
            n={4}
            source={source}
            setSource={setSource}
            lists={lists}
            listId={listId}
            setListId={setListId}
            numbersText={numbersText}
            setNumbersText={setNumbersText}
            parsedCount={parsedNumbers.length}
          />
        </div>
      ) : (
        <Card>
          <CardContent className="py-3 flex items-center gap-3 flex-wrap text-sm">
            <span className="flex items-center gap-1.5"><Voicemail className="h-4 w-4 text-orange-500" /> {selectedVm?.name}</span>
            <span className="text-slate-300">·</span>
            <span className="flex items-center gap-1.5"><MessageSquare className="h-4 w-4 text-sky-500" /> {selectedGreeting?.name}</span>
            <span className="text-slate-300">·</span>
            <span className="flex items-center gap-1.5"><FileAudio className="h-4 w-4 text-indigo-500" /> {selectedGreeting2?.name || '—'}</span>
            <span className="text-slate-300">·</span>
            <span className="flex items-center gap-1.5">
              {source === 'list'
                ? <><ListChecks className="h-4 w-4 text-emerald-500" /> <span className="font-medium">{selectedList?.name}</span> <span className="text-slate-500">({selectedList?.dialable_leads} dialable)</span></>
                : <><Hash className="h-4 w-4 text-emerald-500" /> <span className="font-medium">Ad-hoc</span> <span className="text-slate-500">({parsedNumbers.length} #s)</span></>}
            </span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-600">{concurrent} concurrent</span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-500">scale {(metrics?.campaign?.current_pacing_scale ?? 1.0).toFixed(2)}×</span>
            <div className="ml-auto flex items-center gap-2">
              {sessionState === 'paused' ? (
                <Button variant="outline" size="sm" onClick={resumeSession} className="border-green-300 text-green-700 hover:bg-green-50">
                  <Play className="h-4 w-4 mr-1" /> Resume
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={pauseSession} disabled={sessionState === 'on_call'}>
                  <Pause className="h-4 w-4 mr-1" /> Pause
                </Button>
              )}
              <Button variant="destructive" size="sm" onClick={stopSession}>
                <Square className="h-4 w-4 mr-1" /> Stop
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Idle: concurrent + Start row */}
      {!running && (
        <Card>
          <CardContent className="py-4 flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-3">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Concurrent calls</Label>
              <div className="flex items-center border rounded-md">
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setConcurrent(Math.max(1, concurrent - 1))}>
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="w-10 text-center font-semibold tabular-nums">{concurrent}</span>
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setConcurrent(Math.min(30, concurrent + 1))}>
                  <PlusIcon className="h-3 w-3" />
                </Button>
              </div>
              <span className="text-xs text-slate-400">1–30 lines · auto-scales 0.5×–3× based on pickup/abandon rate</span>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={aiAgentEnabled}
                onChange={(e) => setAiAgentEnabled(e.target.checked)}
              />
              <span>Send to AI Agent (Phase II)</span>
              <span className="text-xs text-slate-400">— currently falls back to Greetings2</span>
            </label>
            <Button size="lg" className="ml-auto px-8" disabled={!canStart} onClick={startCalls}>
              <PhoneCall className="h-5 w-5 mr-2" />
              Start Calls
              <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Running: stats + in-flight + ticker */}
      {running && (
        <>
          <StatGrid
            dialing={inFlight.filter(a => ['dialing','ringing'].includes(a.status)).length}
            humans={humansReached}
            vmDropped={vmDropped}
            vmRate={vmRate}
            queue={metrics?.queue_depth ?? 0}
            badCount={badCount}
          />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>In flight</span>
                <Badge variant="secondary">{inFlight.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {inFlight.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-6">Waiting for next dial…</div>
              ) : (
                <div className="space-y-1">
                  {inFlight.map(a => {
                    const isHuman = a.status === 'parked' && !a.handed_off_at;
                    // Greeting1 has finished playing — open a 3s BARGE window
                    // unless the operator is already on another call.
                    const gEnd = greetingEndedAt[a.id];
                    const sinceGreetingMs = gEnd ? (Date.now() - gEnd) : -1;
                    const inBargeWindow = gEnd && sinceGreetingMs >= 0 && sinceGreetingMs < bargeWindowMs;
                    const showBarge = isHuman && inBargeWindow && !operatorBusy;
                    const m = STATUS_META[showBarge ? 'barge' : a.status] || STATUS_META.dialing;
                    return (
                      <div
                        key={a.id}
                        className={`flex items-center gap-3 px-3 py-2 rounded border ${isHuman ? 'border-green-400 bg-green-50' : 'border-slate-100 hover:bg-slate-50'} transition`}>
                        <Badge
                          className={`${m.badge} ${m.pulse ? 'animate-pulse' : ''} w-20 justify-center`}
                          onClick={showBarge ? () => answerCall(a.id) : undefined}
                          title={showBarge ? 'Click to join the call' : undefined}
                        >
                          {m.label}
                        </Badge>
                        <span className="flex-1 truncate text-sm font-medium">{a.lead_name || '—'}</span>
                        <span className="text-xs text-slate-500 tabular-nums font-mono w-32 text-right">{a.to_number}</span>
                        <span className="text-xs text-slate-400 tabular-nums font-mono w-14 text-right">{fmtTime(secondsSince(a.started_at))}</span>
                        {showBarge ? (
                          <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => releaseCall(a.id)}>
                            <PhoneOff className="h-3.5 w-3.5 mr-1" /> End
                          </Button>
                        ) : isHuman ? (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => answerCall(a.id)}>
                            <PhoneCall className="h-3.5 w-3.5 mr-1" /> Answer
                          </Button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Recent outcomes</span>
                <span className="text-xs text-slate-400 font-normal">last 5m</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recent.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-4">—</div>
              ) : (
                <div className="space-y-1 max-h-72 overflow-y-auto scrollbar-thin">
                  {recent.map(d => {
                    const m = STATUS_META[d.status] || STATUS_META.cancelled;
                    return (
                      <div key={d.id} className="flex items-center gap-3 px-2 py-1.5 text-sm">
                        <span className="text-xs text-slate-400 font-mono tabular-nums w-20">{timeOf(d.ended_at)}</span>
                        <Badge className={`${m.badge} w-20 justify-center`}>{m.label}</Badge>
                        <span className="flex-1 truncate">{d.lead_name || d.to_number}</span>
                        <span className="text-xs text-slate-400 font-mono tabular-nums w-14 text-right">
                          {d.duration_seconds ? `${Math.round(d.duration_seconds)}s` : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {(activeCall || logCallOpen) && callMeta && (
        <LeadContextCard meta={callMeta} lead={activeLead} live={!!activeCall} />
      )}

      {activeCall && (
        <DialerBridgeCallPanel
          activeCall={activeCall}
          onHangup={async () => {
            const aid = activeCall.attempt_id;
            window.dispatchEvent(new CustomEvent('jtcrm-dialer-bridge-hangup'));
            try { await axios.post(`${API}/api/ai-calls/attempts/${aid}/hangup`); } catch {/* best-effort */}
            const duration = Math.max(0, Math.floor((Date.now() - (activeCall.startedAt || Date.now())) / 1000));
            handleCallEnded(duration, null);
          }}
        />
      )}

      <LogCallDialog
        open={logCallOpen}
        onClose={handleLogCallClose}
        lead={activeLead || (callMeta ? {
          id: callMeta.leadId,
          first_name: (callMeta.leadName || '').split(' ')[0] || '',
          last_name: (callMeta.leadName || '').split(' ').slice(1).join(' '),
          company_name: callMeta.companyName,
          phone: callMeta.phone,
        } : null)}
        callForm={callForm || EMPTY_CALL_FORM}
        setCallForm={setCallForm}
        onSubmit={handleLogCallSubmit}
        saving={savingLog}
        templates={[]}
      />
    </div>
  );
}

function DialerBridgeCallPanel({ activeCall, onHangup }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const start = activeCall.startedAt || Date.now();
    const tick = () => setSeconds(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [activeCall.startedAt]);
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return (
    <div
      className="fixed top-1/2 right-6 -translate-y-1/2 z-[150] w-80 shadow-2xl rounded-2xl overflow-hidden bg-white"
      data-testid="dialer-bridge-panel"
    >
      <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-5 py-4 flex items-center justify-between text-white">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
            <PhoneCall className="h-5 w-5" />
          </div>
          <div>
            <p className="font-bold text-sm truncate max-w-[160px]">{activeCall.leadName || 'On Call'}</p>
            <p className="text-2xl font-mono font-bold opacity-95">{mm}:{ss}</p>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-2">
        {activeCall.companyName && (
          <p className="text-sm text-slate-600 text-center truncate">{activeCall.companyName}</p>
        )}
        {activeCall.phone && (
          <p className="text-sm font-mono text-slate-600 text-center">{activeCall.phone}</p>
        )}
        <Button
          onClick={onHangup}
          className="w-full mt-2 gap-2 bg-red-500 hover:bg-red-600 text-white"
          data-testid="dialer-bridge-hangup"
        >
          <PhoneOff className="h-4 w-4" />
          End Call
        </Button>
      </div>
    </div>
  );
}

function LeadContextCard({ meta, lead, live }) {
  const name = lead
    ? (`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.name || meta.leadName)
    : meta.leadName;
  return (
    <Card className={`fixed top-20 left-4 z-[100] w-80 shadow-2xl bg-white ${live ? 'border-2 border-green-400' : 'border border-slate-200'}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-800">
          {live
            ? <PhoneCall className="h-4 w-4 text-green-600 animate-pulse" />
            : <User className="h-4 w-4 text-slate-500" />}
          <span className="truncate">{live ? 'Live with' : 'Just spoke with'} {name}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-1.5 pt-0">
        {lead?.title && <div className="text-slate-700 truncate">{lead.title}</div>}
        {meta.companyName && <div className="text-indigo-600 font-medium truncate">{meta.companyName}</div>}
        {(lead?.work_email || lead?.email) && (
          <div className="text-xs text-slate-500 truncate">{lead?.work_email || lead?.email}</div>
        )}
        <div className="text-xs font-mono text-slate-500">{meta.phone}</div>
        {lead?.notes && (
          <div className="text-xs text-slate-600 mt-2 max-h-32 overflow-y-auto border-t pt-2 whitespace-pre-wrap">
            {String(lead.notes).slice(0, 400)}
          </div>
        )}
        {meta.leadId && (
          <a
            href={`/admin/leads/${meta.leadId}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-indigo-600 hover:underline mt-2 inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3 w-3" /> Open full lead
          </a>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Subcomponents ─────────────────────────── */

function HumanAlert({ parked, bargeGrace, greetingEndedAt, onAnswer, onRelease }) {
  const sec = secondsSince(parked.answered_at);
  const gEnded = !!greetingEndedAt;
  const remainingMs = gEnded ? Math.max(0, (bargeGrace * 1000) - (Date.now() - greetingEndedAt)) : null;
  const remainingSec = remainingMs == null ? null : Math.ceil(remainingMs / 1000);
  return (
    <Card className="border-green-400 border-2 bg-green-50" style={{ animation: 'dialer-pulse-ring 1.2s cubic-bezier(0.4,0,0.6,1) infinite' }}>
      <CardContent className="py-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="h-12 w-12 rounded-full bg-green-500 text-white flex items-center justify-center shrink-0 animate-pulse">
              <PhoneCall className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-green-700 font-semibold">
                {gEnded
                  ? `Human answered · BARGE window · ${remainingSec}s left`
                  : `Human answered · Greetings1 playing · ${bargeGrace}s window after`}
              </div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xl font-semibold text-slate-900 truncate">{parked.lead_name || 'Lead'}</span>
                <span className="text-sm text-slate-500 font-mono tabular-nums">{parked.to_number}</span>
                {parked.lead_company && <span className="text-sm text-slate-500 truncate">· {parked.lead_company}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="font-mono text-2xl text-green-700 tabular-nums w-16 text-right">{fmtTime(sec)}</div>
            <Button size="lg" className="h-12 px-6 bg-green-600 hover:bg-green-700 text-base" onClick={() => onAnswer(parked.id)}>
              <PhoneCall className="h-5 w-5 mr-2" /> Answer
            </Button>
            <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => onRelease(parked.id)}>
              <PhoneOff className="h-4 w-4 mr-1.5" /> Hangup / End
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SetupCard({ n, title, subtitle, icon, clips, currentId, onPick, uploading, onUpload, onRecord, onDelete, onRename, onLoadCampaigns }) {
  const current = clips.find(c => c.id === currentId);
  const fileRef = useRef();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
        setRecording(false);
        clearInterval(tickRef.current);
        setElapsed(0);
        if (blob.size > 0) await onRecord(blob);
      };
      rec.start();
      setRecording(true);
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } catch (e) {
      toast.error('Microphone permission denied');
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch {}
  };

  // Hard cap at 60s
  useEffect(() => {
    if (recording && elapsed >= 60) stopRecording();
  }, [recording, elapsed]);

  // Cleanup
  useEffect(() => () => {
    clearInterval(tickRef.current);
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
  }, []);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="h-6 w-6 p-0 justify-center text-slate-500 font-mono">{n}</Badge>
            {icon}
          </div>
          {current && <Badge variant="secondary" className="text-[10px]">Selected</Badge>}
        </div>
        <CardTitle className="text-base mt-2">{title}</CardTitle>
        <CardDescription className="text-xs">{subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {clips.length > 0 && (
          <ClipCombobox clips={clips} value={currentId} onPick={onPick} onDelete={onDelete} onRename={onRename} onLoadCampaigns={onLoadCampaigns} />
        )}
        {current && (
          <div className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1.5">
            <audio controls src={`${API}/api/ai-calls/audio-media/${current.id}/audio`} className="h-8 flex-1" />
          </div>
        )}
        <div className="space-y-2">
          {recording ? (
            <Button size="sm" variant="destructive" onClick={stopRecording} className="w-full">
              <StopCircle className="h-4 w-4 mr-2 animate-pulse" />
              Stop recording · {fmtTime(elapsed)}
            </Button>
          ) : (
            <Button size="sm" variant="default" onClick={startRecording} disabled={uploading} className="w-full bg-red-600 hover:bg-red-700">
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mic className="h-4 w-4 mr-2" />}
              Record
            </Button>
          )}
          <input ref={fileRef} type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav" className="hidden"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading || recording} className="w-full">
            <Upload className="h-4 w-4 mr-2" /> Upload file
          </Button>
          <p className="text-[11px] text-slate-400">MP3 / WAV upload or browser-recorded audio · ≤ 2 MB · ≤ 60 s · saved to your library</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ClipCombobox({ clips, value, onPick, onDelete, onRename, onLoadCampaigns }) {
  const [open, setOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [conflict, setConflict] = useState(null);  // { clip, campaigns, loading }
  const msg = useAppMessage();
  const current = clips.find(c => c.id === value);

  const handleDelete = async (e, clip) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onDelete) return;
    const ok = await msg.confirm(`Delete "${clip.name}"?`, 'This cannot be undone.', { destructive: true, confirmLabel: 'Delete' });
    if (!ok) return;
    setDeletingId(clip.id);
    try {
      const res = await onDelete(clip);
      if (res && res.conflict) {
        setConflict({ clip, campaigns: [], loading: true });
        const campaigns = onLoadCampaigns ? await onLoadCampaigns(clip) : [];
        setConflict({ clip, campaigns, loading: false });
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleRename = async (e, clip) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onRename) return;
    const next = await msg.prompt('Rename recording', '', { defaultValue: clip.name || '', confirmLabel: 'Rename' });
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === clip.name) return;
    await onRename(clip, trimmed);
  };

  const handleForceDelete = async () => {
    if (!conflict?.clip) return;
    const ok = await msg.confirm(
      `Force delete "${conflict.clip.name}"?`,
      `This will remove the clip and clear it from ${conflict.campaigns.length} campaign(s). Existing campaigns will keep running but lose this audio.`,
      { destructive: true, confirmLabel: 'Force delete' },
    );
    if (!ok) return;
    setDeletingId(conflict.clip.id);
    try {
      await onDelete(conflict.clip, { force: true });
      setConflict(null);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
                className="w-full justify-between font-normal">
          <span className="truncate">
            {current
              ? `${current.name}${current.duration_seconds ? ` · ${current.duration_seconds.toFixed(1)}s` : ''}`
              : 'Select saved recording…'}
          </span>
          <ChevronRight className="h-4 w-4 opacity-50 shrink-0 rotate-90" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
        <Command>
          <CommandInput placeholder="Search recordings…" autoFocus />
          <CommandList>
            <CommandEmpty>No recordings found.</CommandEmpty>
            <CommandGroup>
              {clips.map(c => (
                <CommandItem
                  key={c.id}
                  value={`${c.name} ${c.id}`}
                  onSelect={() => { onPick(c.id); setOpen(false); }}
                  className="cursor-pointer group">
                  <div className="flex items-center gap-2 w-full">
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${c.id === value ? 'bg-indigo-500' : 'bg-slate-300'}`} />
                    <span className="flex-1 truncate">{c.name}</span>
                    {c.duration_seconds && (
                      <span className="text-[10px] text-slate-400 font-mono tabular-nums">{c.duration_seconds.toFixed(1)}s</span>
                    )}
                    {onRename && (
                      <button
                        type="button"
                        aria-label={`Rename ${c.name}`}
                        title="Rename"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => handleRename(e, c)}
                        className="opacity-0 group-hover:opacity-100 hover:bg-slate-100 rounded p-1">
                        <Pencil className="h-3 w-3 text-slate-400 hover:text-slate-700" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        aria-label={`Delete ${c.name}`}
                        title="Delete"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => handleDelete(e, c)}
                        disabled={deletingId === c.id}
                        className="opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded p-1 -mr-1 disabled:opacity-50">
                        {deletingId === c.id
                          ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                          : <Trash2 className="h-3 w-3 text-slate-400 hover:text-red-600" />}
                      </button>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>

    <Dialog open={!!conflict} onOpenChange={(o) => { if (!o) setConflict(null); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Clip is in use
          </DialogTitle>
          <DialogDescription>
            "{conflict?.clip?.name}" is referenced by the following campaign(s). You can force-delete to remove the clip and clear it from these campaigns, or open a campaign to delete it first.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-auto border rounded">
          {conflict?.loading ? (
            <div className="flex items-center justify-center p-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : !conflict?.campaigns?.length ? (
            <div className="p-4 text-sm text-slate-500">No campaigns found (the clip may have been freed already — try delete again).</div>
          ) : (
            <ul className="divide-y">
              {conflict.campaigns.map(c => (
                <li key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className="flex-1 truncate">{c.name || '(unnamed campaign)'}</span>
                  <Badge variant="outline" className="text-[10px]">{c.status || 'unknown'}</Badge>
                  {c.roles?.length > 0 && (
                    <span className="text-[10px] text-slate-400">{c.roles.join(', ')}</span>
                  )}
                  <a
                    href={`/admin/ai-calls`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 text-xs"
                  >
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConflict(null)}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleForceDelete}
            disabled={conflict?.loading || deletingId === conflict?.clip?.id}
          >
            {deletingId === conflict?.clip?.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Force delete clip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function TargetCard({ n, source, setSource, lists, listId, setListId, numbersText, setNumbersText, parsedCount }) {
  const currentList = lists.find(l => l.id === listId);
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="h-6 w-6 p-0 justify-center text-slate-500 font-mono">{n}</Badge>
            <ListChecks className="h-5 w-5 text-emerald-500" />
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={source === 'list' ? 'default' : 'outline'}
              className="h-7 text-[10px]"
              onClick={() => setSource('list')}>
              <ListChecks className="h-3 w-3 mr-1" /> List
            </Button>
            <Button
              size="sm"
              variant={source === 'numbers' ? 'default' : 'outline'}
              className="h-7 text-[10px]"
              onClick={() => setSource('numbers')}>
              <Hash className="h-3 w-3 mr-1" /> Numbers
            </Button>
          </div>
        </div>
        <CardTitle className="text-base mt-2">Who to dial</CardTitle>
        <CardDescription className="text-xs">
          {source === 'list' ? 'Pick a saved list' : 'Paste phone numbers to test — saved for next time'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {source === 'list' ? (
          <>
            <Select value={listId || ''} onValueChange={setListId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a list..." />
              </SelectTrigger>
              <SelectContent>
                {lists.map(l => (
                  <SelectItem key={l.id} value={l.id}>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: l.color || '#6366f1' }} />
                      {l.name}
                      <span className="text-slate-400 text-xs">· {l.dialable_leads}/{l.total_leads}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentList && (
              <div className="text-xs text-slate-500 bg-slate-50 rounded px-3 py-2">
                <div className="font-medium text-slate-700">{currentList.dialable_leads} dialable</div>
                <div className="text-slate-400">of {currentList.total_leads} total · DNC / bad / unreachable excluded</div>
              </div>
            )}
            {lists.length === 0 && <p className="text-xs text-slate-400">No lists yet.</p>}
          </>
        ) : (
          <>
            <Textarea
              rows={6}
              placeholder={`+15551234567\n+15557654321\n...`}
              className="font-mono text-xs"
              value={numbersText}
              onChange={(e) => setNumbersText(e.target.value)}
            />
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">
                {parsedCount} valid number{parsedCount !== 1 ? 's' : ''}
              </span>
              <span className="text-slate-400">One per line · any common format</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ListCard({ n, title, subtitle, icon, lists, value, onChange }) {
  const current = lists.find(l => l.id === value);
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="h-6 w-6 p-0 justify-center text-slate-500 font-mono">{n}</Badge>
            {icon}
          </div>
          {current && <Badge variant="secondary" className="text-[10px]">Selected</Badge>}
        </div>
        <CardTitle className="text-base mt-2">{title}</CardTitle>
        <CardDescription className="text-xs">{subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select a list..." />
          </SelectTrigger>
          <SelectContent>
            {lists.map(l => (
              <SelectItem key={l.id} value={l.id}>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: l.color || '#6366f1' }} />
                  <span>{l.name}</span>
                  <span className="text-slate-400 text-xs">· {l.dialable_leads} dialable / {l.total_leads} total</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {current && (
          <div className="text-xs text-slate-500 bg-slate-50 rounded px-3 py-2">
            <div className="font-medium text-slate-700">{current.dialable_leads} dialable</div>
            <div className="text-slate-400">of {current.total_leads} total · excludes DNC, bad numbers, unreachable</div>
          </div>
        )}
        {lists.length === 0 && (
          <p className="text-xs text-slate-400">No lists yet. Create one on the Lists page.</p>
        )}
      </CardContent>
    </Card>
  );
}

function StatGrid({ dialing, humans, vmDropped, vmRate, queue, badCount }) {
  const cells = [
    { label: 'Dialing',      value: dialing,    icon: <PhoneCall className="h-4 w-4 text-yellow-500" /> },
    { label: 'Humans',       value: humans,     icon: <PhoneCall className="h-4 w-4 text-green-600" /> },
    { label: 'VM dropped',   value: vmDropped,  icon: <Voicemail className="h-4 w-4 text-orange-500" /> },
    { label: 'VM rate',      value: `${vmRate}%`, icon: <Radio className="h-4 w-4 text-indigo-500" /> },
    { label: 'Queue',        value: queue,      icon: <ListChecks className="h-4 w-4 text-slate-500" /> },
    { label: 'Bad #',        value: badCount,   icon: <PhoneOff className="h-4 w-4 text-red-500" /> },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {cells.map(c => (
        <Card key={c.label}>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-slate-50 flex items-center justify-center shrink-0">
              {c.icon}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</div>
              <div className="text-xl font-semibold tabular-nums">{c.value}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
